'use client';

import { useState, useEffect } from 'react';
import { useTranslations } from 'next-intl';

export default function CanadianMortgageCalculator() {
  const t = useTranslations("tools.canadianMortgageCalculator");
  const [screenSize, setScreenSize] = useState('mobile');

  // Calculator state
  const [inputs, setInputs] = useState({
    homePrice: 800000,
    downPaymentPercent: 20,
    loanTerm: 25,
    interestRate: 5,
    paymentFrequency: 12,
    includeOptional: true,
    propertyTaxRate: 0.3,
    homeInsurance: 2500,
    condoFee: 0,
    otherCosts: 6000,
  });

  const [results, setResults] = useState<any>(null);
  const [schedule, setSchedule] = useState<any[]>([]);

  useEffect(() => {
    const updateScreenSize = () => {
      const width = window.innerWidth;
      if (width >= 1024) {
        setScreenSize('desktop');
      } else if (width >= 768) {
        setScreenSize('tablet');
      } else {
        setScreenSize('mobile');
      }
    };

    updateScreenSize();
    window.addEventListener('resize', updateScreenSize);
    return () => window.removeEventListener('resize', updateScreenSize);
  }, []);

  const getResponsiveStyle = (baseStyle: any, mobileOverrides?: any, tabletOverrides?: any, desktopOverrides?: any) => {
    let style = { ...baseStyle };

    if (screenSize === 'mobile' && mobileOverrides) {
      style = { ...style, ...mobileOverrides };
    } else if (screenSize === 'tablet' && tabletOverrides) {
      style = { ...style, ...tabletOverrides };
    } else if (screenSize === 'desktop' && desktopOverrides) {
      style = { ...style, ...desktopOverrides };
    }

    return style;
  };

  const colors = {
    primary: '#ff5a70',
    primaryHover: '#f63b3b',
    secondary: '#ffc400',
    accent: '#ff6b95',
    textPrimary: '#2d3748',
    textSecondary: '#4a5568',
    glassBg: 'rgba(255, 255, 255, 0.6)',
    glassBorder: 'rgba(255, 200, 180, 0.2)',
  };

  const formatCurrency = (amount: number) => {
    return '$' + Math.abs(amount).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  };

  const getCMHCRate = (downPaymentPercent: number) => {
    if (downPaymentPercent >= 20) return 0;
    if (downPaymentPercent >= 15) return 0.028;
    if (downPaymentPercent >= 10) return 0.031;
    if (downPaymentPercent >= 5) return 0.04;
    return 0;
  };

  const calculateMortgage = (principal: number, annualRate: number, years: number, paymentsPerYear: number) => {
    const semiAnnualRate = Math.pow(1 + annualRate / 2, 2 / paymentsPerYear) - 1;
    const totalPayments = years * paymentsPerYear;

    if (annualRate === 0) {
      return principal / totalPayments;
    }

    const payment = principal * (semiAnnualRate * Math.pow(1 + semiAnnualRate, totalPayments)) /
                   (Math.pow(1 + semiAnnualRate, totalPayments) - 1);

    return payment;
  };

  const generateAmortizationSchedule = (principal: number, annualRate: number, years: number, paymentsPerYear: number, payment: number) => {
    const schedule = [];
    const semiAnnualRate = Math.pow(1 + annualRate / 2, 2 / paymentsPerYear) - 1;
    let balance = principal;
    let yearData = { year: 1, principalPaid: 0, interestPaid: 0, totalPaid: 0, balance: 0 };

    for (let i = 1; i <= years * paymentsPerYear; i++) {
      const interestPayment = balance * semiAnnualRate;
      const principalPayment = payment - interestPayment;
      balance -= principalPayment;

      yearData.principalPaid += principalPayment;
      yearData.interestPaid += interestPayment;
      yearData.totalPaid += payment;

      if (i % paymentsPerYear === 0 || i === years * paymentsPerYear) {
        yearData.balance = Math.max(0, balance);
        schedule.push({...yearData});
        yearData = {
          year: Math.floor(i / paymentsPerYear) + 1,
          principalPaid: 0,
          interestPaid: 0,
          totalPaid: 0,
          balance: 0
        };
      }
    }

    return schedule;
  };

  const handleCalculate = (e: React.FormEvent) => {
    e.preventDefault();

    const downPaymentAmount = inputs.homePrice * (inputs.downPaymentPercent / 100);
    let loanAmount = inputs.homePrice - downPaymentAmount;

    const cmhcRate = getCMHCRate(inputs.downPaymentPercent);
    const cmhcPremium = loanAmount * cmhcRate;
    loanAmount += cmhcPremium;

    const mortgagePayment = calculateMortgage(loanAmount, inputs.interestRate / 100, inputs.loanTerm, inputs.paymentFrequency);

    const paymentsPerMonth = inputs.paymentFrequency / 12;
    const monthlyMortgagePayment = mortgagePayment * paymentsPerMonth;

    let monthlyPropertyTax = 0;
    let monthlyHomeIns = 0;
    let monthlyCondo = 0;
    let monthlyOther = 0;

    if (inputs.includeOptional) {
      monthlyPropertyTax = (inputs.homePrice * inputs.propertyTaxRate / 100) / 12;
      monthlyHomeIns = inputs.homeInsurance / 12;
      monthlyCondo = inputs.condoFee / 12;
      monthlyOther = inputs.otherCosts / 12;
    }

    const totalMonthlyPayment = monthlyMortgagePayment + monthlyPropertyTax +
                               monthlyHomeIns + monthlyCondo + monthlyOther;

    const totalInterest = (mortgagePayment * inputs.paymentFrequency * inputs.loanTerm) - loanAmount;
    const totalPayments = mortgagePayment * inputs.paymentFrequency * inputs.loanTerm;

    const amortizationSchedule = generateAmortizationSchedule(loanAmount, inputs.interestRate / 100, inputs.loanTerm, inputs.paymentFrequency, mortgagePayment);

    setResults({
      totalMonthlyPayment,
      monthlyMortgagePayment,
      monthlyPropertyTax,
      monthlyHomeIns,
      monthlyCondo,
      monthlyOther,
      loanAmount,
      downPaymentAmount,
      totalInterest,
      totalPayments,
      cmhcPremium,
    });

    setSchedule(amortizationSchedule);
  };

  const containerStyle = getResponsiveStyle(
    { maxWidth: '1200px', margin: '0 auto', position: 'relative' as const, zIndex: 1 },
    { padding: '1rem' },
    { padding: '1.5rem' },
    { padding: '2rem' }
  );

  const headerStyle = getResponsiveStyle(
    { textAlign: 'center' as const, marginBottom: '2rem' },
    {},
    { marginBottom: '2.5rem' },
    { marginBottom: '3rem' }
  );

  const h1Style = getResponsiveStyle(
    { fontWeight: 700, color: colors.textPrimary, marginBottom: '1rem', lineHeight: 1.2 },
    { fontSize: '2rem' },
    { fontSize: '2.5rem' },
    { fontSize: '3rem' }
  );

  const summaryTextStyle = getResponsiveStyle(
    { color: colors.textSecondary, lineHeight: 1.6 },
    { fontSize: '1rem' },
    { fontSize: '1.125rem' },
    { fontSize: '1.25rem' }
  );

  const calculatorToolStyle = getResponsiveStyle(
    {
      background: 'linear-gradient(135deg, rgba(240, 249, 255, 0.9) 0%, rgba(224, 242, 254, 0.9) 100%)',
      borderRadius: '1rem',
      marginBottom: '2rem',
      boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
    },
    { padding: '1.5rem 1rem' },
    { padding: '2rem 1.5rem', borderRadius: '1.25rem' },
    { padding: '3rem 2rem', borderRadius: '1.5rem' }
  );

  const toolContainerStyle = getResponsiveStyle(
    { maxWidth: '100%', margin: '0 auto' },
    {},
    { maxWidth: '900px' },
    { maxWidth: '1100px' }
  );

  const toolHeaderStyle = getResponsiveStyle(
    { textAlign: 'center' as const, marginBottom: '1.5rem' },
    {},
    { marginBottom: '2rem' },
    { marginBottom: '2.5rem' }
  );

  const h2Style = getResponsiveStyle(
    { fontWeight: 600, color: colors.textPrimary, marginBottom: '1rem' },
    { fontSize: '1.5rem' },
    { fontSize: '1.75rem' },
    { fontSize: '2rem' }
  );

  const calculatorLayoutStyle = getResponsiveStyle(
    { display: 'grid', gap: '1.5rem', marginBottom: '2rem' },
    { gridTemplateColumns: '1fr' },
    { gridTemplateColumns: '1fr' },
    { gridTemplateColumns: '1fr 1fr', gap: '2rem', marginBottom: '3rem' }
  );

  const formCardStyle = getResponsiveStyle(
    {
      background: 'white',
      borderRadius: '1rem',
      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
    },
    { padding: '1.5rem' },
    { padding: '1.75rem' },
    { padding: '2rem' }
  );

  const formGroupStyle = getResponsiveStyle(
    { marginBottom: '1rem' },
    {},
    { marginBottom: '1.25rem' },
    { marginBottom: '1.5rem' }
  );

  const labelStyle = getResponsiveStyle(
    { display: 'block', fontWeight: 600, color: '#334155', marginBottom: '0.5rem' },
    { fontSize: '0.9rem' },
    { fontSize: '0.95rem' },
    { fontSize: '1rem' }
  );

  const inputStyle = getResponsiveStyle(
    {
      width: '100%',
      border: '2px solid #e2e8f0',
      borderRadius: '0.5rem',
      fontSize: '1rem',
      transition: 'all 0.3s ease',
      boxSizing: 'border-box' as const,
    },
    { padding: '0.75rem' },
    { padding: '0.8rem' },
    { padding: '0.875rem' }
  );

  const selectStyle = { ...inputStyle };

  const inputWithUnitStyle = { position: 'relative' as const };

  const inputUnitStyle = {
    position: 'absolute' as const,
    right: '1rem',
    top: '50%',
    transform: 'translateY(-50%)',
    color: '#64748b',
    fontSize: '0.9rem',
    pointerEvents: 'none' as const,
  };

  const checkboxGroupStyle = {
    display: 'flex',
    alignItems: 'center',
    marginBottom: '1.5rem',
  };

  const checkboxStyle = {
    width: '1.25rem',
    height: '1.25rem',
    marginRight: '0.5rem',
    cursor: 'pointer',
  };

  const checkboxLabelStyle = {
    margin: 0,
    cursor: 'pointer',
    fontWeight: 600,
    color: '#2563eb',
  };

  const optionalSectionStyle = {
    borderTop: '2px solid #e2e8f0',
    paddingTop: '1.5rem',
    marginTop: '1.5rem',
  };

  const calculateBtnStyle = getResponsiveStyle(
    {
      width: '100%',
      background: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)',
      color: 'white',
      border: 'none',
      borderRadius: '0.5rem',
      fontWeight: 600,
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      marginTop: '1rem',
    },
    { padding: '0.875rem 1.5rem', fontSize: '1rem' },
    { padding: '1rem 2rem', fontSize: '1.1rem' },
    { padding: '1.125rem 2rem', fontSize: '1.125rem' }
  );

  const resultsCardStyle = getResponsiveStyle(
    {
      background: 'white',
      borderRadius: '1rem',
      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
    },
    { padding: '1.5rem' },
    { padding: '1.75rem' },
    { padding: '2rem' }
  );

  const mainResultStyle = getResponsiveStyle(
    {
      background: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)',
      color: 'white',
      borderRadius: '0.75rem',
      textAlign: 'center' as const,
      marginBottom: '1.5rem',
    },
    { padding: '1.25rem' },
    { padding: '1.375rem' },
    { padding: '1.5rem' }
  );

  const mainResultLabelStyle = {
    fontSize: '0.9rem',
    opacity: 0.9,
    marginBottom: '0.5rem',
  };

  const mainResultValueStyle = getResponsiveStyle(
    { fontWeight: 700 },
    { fontSize: '2rem' },
    { fontSize: '2.25rem' },
    { fontSize: '2.5rem' }
  );

  const resultRowStyle = {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '0.75rem 0',
    borderBottom: '1px solid #e2e8f0',
  };

  const resultRowHeaderStyle = {
    ...resultRowStyle,
    fontWeight: 600,
    color: '#334155',
    borderBottom: '2px solid #cbd5e1',
  };

  const resultRowTotalStyle = {
    display: 'flex',
    justifyContent: 'space-between',
    background: '#f1f5f9',
    padding: '1rem',
    borderRadius: '0.5rem',
    fontWeight: 700,
    color: '#0f172a',
    marginTop: '0.5rem',
  };

  const resultLabelStyle = { color: '#475569' };
  const resultValueStyle = { fontWeight: 600, color: '#0f172a' };

  const summaryGridStyle = getResponsiveStyle(
    { display: 'grid', gap: '1rem', marginTop: '1.5rem' },
    { gridTemplateColumns: '1fr' },
    { gridTemplateColumns: '1fr 1fr' },
    { gridTemplateColumns: '1fr 1fr' }
  );

  const summaryItemStyle = {
    background: '#f8fafc',
    padding: '1rem',
    borderRadius: '0.5rem',
    borderLeft: '4px solid #2563eb',
  };

  const summaryItemLabelStyle = {
    fontSize: '0.85rem',
    color: '#64748b',
    marginBottom: '0.25rem',
  };

  const summaryItemValueStyle = getResponsiveStyle(
    { fontWeight: 700, color: '#0f172a' },
    { fontSize: '1.1rem' },
    { fontSize: '1.2rem' },
    { fontSize: '1.25rem' }
  );

  const amortizationSectionStyle = getResponsiveStyle(
    {
      background: 'white',
      borderRadius: '1rem',
      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
      marginTop: '2rem',
    },
    { padding: '1.5rem' },
    { padding: '1.75rem' },
    { padding: '2rem' }
  );

  const scheduleTableStyle = getResponsiveStyle(
    {
      width: '100%',
      borderCollapse: 'collapse' as const,
      marginTop: '1.5rem',
    },
    { fontSize: '0.8rem' },
    { fontSize: '0.85rem' },
    { fontSize: '0.9rem' }
  );

  const tableHeaderStyle = {
    background: '#f1f5f9',
    padding: '0.75rem',
    textAlign: 'left' as const,
    fontWeight: 600,
    color: '#334155',
    borderBottom: '2px solid #cbd5e1',
  };

  const tableCellStyle = {
    padding: '0.75rem',
    borderBottom: '1px solid #e2e8f0',
  };

  const glassCardStyle = getResponsiveStyle(
    {
      background: colors.glassBg,
      backdropFilter: 'blur(20px) saturate(180%)',
      WebkitBackdropFilter: 'blur(20px) saturate(180%)',
      border: `1px solid ${colors.glassBorder}`,
      borderRadius: '1rem',
      boxShadow: '0 8px 32px 0 rgba(255, 90, 112, 0.08)',
      marginBottom: '2rem',
    },
    { padding: '1.5rem' },
    { padding: '2rem' },
    { padding: '2.5rem' }
  );

  const paragraphStyle = getResponsiveStyle(
    { lineHeight: 1.7, color: colors.textSecondary, marginBottom: '1rem' },
    { fontSize: '1rem' },
    { fontSize: '1.05rem' },
    { fontSize: '1.1rem' }
  );

  const listStyle = {
    marginLeft: '1.5rem',
    marginBottom: '1rem',
  };

  const listItemStyle = getResponsiveStyle(
    { marginBottom: '0.75rem', lineHeight: 1.6, color: colors.textSecondary },
    { fontSize: '1rem' },
    { fontSize: '1.05rem' },
    { fontSize: '1.1rem' }
  );

  const h3Style = getResponsiveStyle(
    { fontWeight: 600, color: colors.textPrimary, marginTop: '1.5rem', marginBottom: '1rem' },
    { fontSize: '1.25rem' },
    { fontSize: '1.35rem' },
    { fontSize: '1.5rem' }
  );

  const faqItemStyle = {
    marginBottom: '1.5rem',
  };

  const faqQuestionStyle = getResponsiveStyle(
    { fontWeight: 600, color: colors.textPrimary, marginBottom: '0.5rem' },
    { fontSize: '1.05rem' },
    { fontSize: '1.1rem' },
    { fontSize: '1.15rem' }
  );

  const faqAnswerStyle = getResponsiveStyle(
    { lineHeight: 1.6, color: colors.textSecondary },
    { fontSize: '0.95rem' },
    { fontSize: '1rem' },
    { fontSize: '1.05rem' }
  );

  const refLinkStyle = {
    color: colors.primary,
    textDecoration: 'none',
    wordBreak: 'break-word' as const,
  };

  return (
    <div style={containerStyle}>
      <header style={headerStyle}>
        <h1 style={h1Style}>{t("title")}</h1>
        <p style={summaryTextStyle}>{t("subtitle")}</p>
      </header>

      <section style={calculatorToolStyle}>
        <div style={toolContainerStyle}>
          <div style={toolHeaderStyle}>
            <h2 style={h2Style}>🏠 {t("toolTitle")}</h2>
            <p style={summaryTextStyle}>{t("toolSubtitle")}</p>
          </div>

          <div style={calculatorLayoutStyle}>
            <div style={formCardStyle}>
              <form onSubmit={handleCalculate}>
                <div style={formGroupStyle}>
                  <label htmlFor="homePrice" style={labelStyle}>{t("homePrice")}</label>
                  <div style={inputWithUnitStyle}>
                    <input
                      type="number"
                      id="homePrice"
                      value={inputs.homePrice}
                      onChange={(e) => setInputs({...inputs, homePrice: parseFloat(e.target.value) || 0})}
                      step="1000"
                      required
                      style={inputStyle}
                    />
                    <span style={inputUnitStyle}>CAD</span>
                  </div>
                </div>

                <div style={formGroupStyle}>
                  <label htmlFor="downPayment" style={labelStyle}>{t("downPayment")}</label>
                  <div style={inputWithUnitStyle}>
                    <input
                      type="number"
                      id="downPayment"
                      value={inputs.downPaymentPercent}
                      onChange={(e) => setInputs({...inputs, downPaymentPercent: parseFloat(e.target.value) || 0})}
                      step="0.1"
                      required
                      style={inputStyle}
                    />
                    <span style={inputUnitStyle}>%</span>
                  </div>
                </div>

                <div style={formGroupStyle}>
                  <label htmlFor="loanTerm" style={labelStyle}>{t("amortizationPeriod")}</label>
                  <div style={inputWithUnitStyle}>
                    <input
                      type="number"
                      id="loanTerm"
                      value={inputs.loanTerm}
                      onChange={(e) => setInputs({...inputs, loanTerm: parseFloat(e.target.value) || 0})}
                      min="1"
                      max="30"
                      required
                      style={inputStyle}
                    />
                    <span style={inputUnitStyle}>{t("years")}</span>
                  </div>
                </div>

                <div style={formGroupStyle}>
                  <label htmlFor="interestRate" style={labelStyle}>{t("interestRate")}</label>
                  <div style={inputWithUnitStyle}>
                    <input
                      type="number"
                      id="interestRate"
                      value={inputs.interestRate}
                      onChange={(e) => setInputs({...inputs, interestRate: parseFloat(e.target.value) || 0})}
                      step="0.01"
                      required
                      style={inputStyle}
                    />
                    <span style={inputUnitStyle}>%</span>
                  </div>
                </div>

                <div style={formGroupStyle}>
                  <label htmlFor="paymentFrequency" style={labelStyle}>{t("paymentFrequency")}</label>
                  <select
                    id="paymentFrequency"
                    value={inputs.paymentFrequency}
                    onChange={(e) => setInputs({...inputs, paymentFrequency: parseFloat(e.target.value)})}
                    style={selectStyle}
                  >
                    <option value="12">{t("monthly")}</option>
                    <option value="26">{t("biWeekly")}</option>
                    <option value="24">{t("semiMonthly")}</option>
                    <option value="52">{t("weekly")}</option>
                  </select>
                </div>

                <div style={optionalSectionStyle}>
                  <div style={checkboxGroupStyle}>
                    <input
                      type="checkbox"
                      id="includeOptional"
                      checked={inputs.includeOptional}
                      onChange={(e) => setInputs({...inputs, includeOptional: e.target.checked})}
                      style={checkboxStyle}
                    />
                    <label htmlFor="includeOptional" style={checkboxLabelStyle}>{t("includeAdditionalCosts")}</label>
                  </div>

                  {inputs.includeOptional && (
                    <div>
                      <div style={formGroupStyle}>
                        <label htmlFor="propertyTax" style={labelStyle}>{t("propertyTax")}</label>
                        <div style={inputWithUnitStyle}>
                          <input
                            type="number"
                            id="propertyTax"
                            value={inputs.propertyTaxRate}
                            onChange={(e) => setInputs({...inputs, propertyTaxRate: parseFloat(e.target.value) || 0})}
                            step="0.01"
                            style={inputStyle}
                          />
                          <span style={inputUnitStyle}>%</span>
                        </div>
                      </div>

                      <div style={formGroupStyle}>
                        <label htmlFor="homeInsurance" style={labelStyle}>{t("homeInsurance")}</label>
                        <div style={inputWithUnitStyle}>
                          <input
                            type="number"
                            id="homeInsurance"
                            value={inputs.homeInsurance}
                            onChange={(e) => setInputs({...inputs, homeInsurance: parseFloat(e.target.value) || 0})}
                            step="100"
                            style={inputStyle}
                          />
                          <span style={inputUnitStyle}>CAD</span>
                        </div>
                      </div>

                      <div style={formGroupStyle}>
                        <label htmlFor="condoFee" style={labelStyle}>{t("condoFee")}</label>
                        <div style={inputWithUnitStyle}>
                          <input
                            type="number"
                            id="condoFee"
                            value={inputs.condoFee}
                            onChange={(e) => setInputs({...inputs, condoFee: parseFloat(e.target.value) || 0})}
                            step="100"
                            style={inputStyle}
                          />
                          <span style={inputUnitStyle}>CAD</span>
                        </div>
                      </div>

                      <div style={formGroupStyle}>
                        <label htmlFor="otherCosts" style={labelStyle}>{t("otherCosts")}</label>
                        <div style={inputWithUnitStyle}>
                          <input
                            type="number"
                            id="otherCosts"
                            value={inputs.otherCosts}
                            onChange={(e) => setInputs({...inputs, otherCosts: parseFloat(e.target.value) || 0})}
                            step="100"
                            style={inputStyle}
                          />
                          <span style={inputUnitStyle}>CAD</span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                <button type="submit" style={calculateBtnStyle}>{t("calculateButton")}</button>
              </form>
            </div>

            {results && (
              <div style={resultsCardStyle}>
                <h3 style={h2Style}>{t("resultsTitle")}</h3>

                <div style={mainResultStyle}>
                  <div style={mainResultLabelStyle}>{t("monthlyPayment")}</div>
                  <div style={mainResultValueStyle}>{formatCurrency(results.totalMonthlyPayment)}</div>
                </div>

                <div>
                  <div style={resultRowHeaderStyle}>
                    <span>{t("component")}</span>
                    <span>{t("perPayment")}</span>
                  </div>
                  <div style={resultRowStyle}>
                    <span style={resultLabelStyle}>{t("mortgagePayment")}</span>
                    <span style={resultValueStyle}>{formatCurrency(results.monthlyMortgagePayment)}</span>
                  </div>
                  {inputs.includeOptional && results.monthlyPropertyTax > 0 && (
                    <div style={resultRowStyle}>
                      <span style={resultLabelStyle}>{t("propertyTaxPayment")}</span>
                      <span style={resultValueStyle}>{formatCurrency(results.monthlyPropertyTax)}</span>
                    </div>
                  )}
                  {inputs.includeOptional && results.monthlyHomeIns > 0 && (
                    <div style={resultRowStyle}>
                      <span style={resultLabelStyle}>{t("homeInsurancePayment")}</span>
                      <span style={resultValueStyle}>{formatCurrency(results.monthlyHomeIns)}</span>
                    </div>
                  )}
                  {inputs.includeOptional && results.monthlyCondo > 0 && (
                    <div style={resultRowStyle}>
                      <span style={resultLabelStyle}>{t("condoFeePayment")}</span>
                      <span style={resultValueStyle}>{formatCurrency(results.monthlyCondo)}</span>
                    </div>
                  )}
                  {inputs.includeOptional && results.monthlyOther > 0 && (
                    <div style={resultRowStyle}>
                      <span style={resultLabelStyle}>{t("otherCostsPayment")}</span>
                      <span style={resultValueStyle}>{formatCurrency(results.monthlyOther)}</span>
                    </div>
                  )}
                  <div style={resultRowTotalStyle}>
                    <span>{t("totalPayment")}</span>
                    <span>{formatCurrency(results.totalMonthlyPayment)}</span>
                  </div>
                </div>

                <div style={summaryGridStyle}>
                  <div style={summaryItemStyle}>
                    <div style={summaryItemLabelStyle}>{t("loanAmount")}</div>
                    <div style={summaryItemValueStyle}>{formatCurrency(results.loanAmount)}</div>
                  </div>
                  <div style={summaryItemStyle}>
                    <div style={summaryItemLabelStyle}>{t("downPaymentAmount")}</div>
                    <div style={summaryItemValueStyle}>{formatCurrency(results.downPaymentAmount)}</div>
                  </div>
                  <div style={summaryItemStyle}>
                    <div style={summaryItemLabelStyle}>{t("totalInterest")}</div>
                    <div style={summaryItemValueStyle}>{formatCurrency(results.totalInterest)}</div>
                  </div>
                  <div style={summaryItemStyle}>
                    <div style={summaryItemLabelStyle}>{t("totalPayments")}</div>
                    <div style={summaryItemValueStyle}>{formatCurrency(results.totalPayments)}</div>
                  </div>
                </div>

                {results.cmhcPremium > 0 && (
                  <div style={{...summaryItemStyle, marginTop: '1rem'}}>
                    <div style={summaryItemLabelStyle}>{t("cmhcInsurance")}</div>
                    <div style={summaryItemValueStyle}>{formatCurrency(results.cmhcPremium)}</div>
                  </div>
                )}
              </div>
            )}
          </div>

          {results && schedule.length > 0 && (
            <div style={amortizationSectionStyle}>
              <h3 style={h2Style}>📊 {t("amortizationSchedule")}</h3>

              <div style={{overflowX: 'auto'}}>
                <table style={scheduleTableStyle}>
                  <thead>
                    <tr>
                      <th style={tableHeaderStyle}>{t("year")}</th>
                      <th style={tableHeaderStyle}>{t("principalPaid")}</th>
                      <th style={tableHeaderStyle}>{t("interestPaid")}</th>
                      <th style={tableHeaderStyle}>{t("totalPaid")}</th>
                      <th style={tableHeaderStyle}>{t("remainingBalance")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {schedule.map((row, index) => (
                      <tr key={index} style={{background: index % 2 === 0 ? '#fff' : '#f8fafc'}}>
                        <td style={tableCellStyle}>{row.year}</td>
                        <td style={tableCellStyle}>{formatCurrency(row.principalPaid)}</td>
                        <td style={tableCellStyle}>{formatCurrency(row.interestPaid)}</td>
                        <td style={tableCellStyle}>{formatCurrency(row.totalPaid)}</td>
                        <td style={tableCellStyle}>{formatCurrency(row.balance)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t("introTitle")}</h2>
        <p style={paragraphStyle}>{t("introParagraph1")}</p>
        <p style={paragraphStyle}>{t("introParagraph2")}</p>
        <p style={paragraphStyle}>{t("introParagraph3")}</p>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t("howToUseTitle")}</h2>
        <ol style={listStyle}>
          <li style={listItemStyle}>{t("howToUseStep1")}</li>
          <li style={listItemStyle}>{t("howToUseStep2")}</li>
          <li style={listItemStyle}>{t("howToUseStep3")}</li>
          <li style={listItemStyle}>{t("howToUseStep4")}</li>
          <li style={listItemStyle}>{t("howToUseStep5")}</li>
          <li style={listItemStyle}>{t("howToUseStep6")}</li>
        </ol>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t("insightsTitle")}</h2>

        <h3 style={h3Style}>{t("insightsStressTestTitle")}</h3>
        <p style={paragraphStyle}>{t("insightsStressTestContent")}</p>

        <h3 style={h3Style}>{t("insightsGdsTdsTitle")}</h3>
        <p style={paragraphStyle}>{t("insightsGdsTdsContent")}</p>
        <ul style={listStyle}>
          <li style={listItemStyle}>{t("insightsGdsFormula")}</li>
          <li style={listItemStyle}>{t("insightsTdsFormula")}</li>
        </ul>

        <h3 style={h3Style}>{t("insightsCmhcTitle")}</h3>
        <p style={paragraphStyle}>{t("insightsCmhcContent")}</p>

        <h3 style={h3Style}>{t("insightsAmortizationTitle")}</h3>
        <p style={paragraphStyle}>{t("insightsAmortizationContent")}</p>

        <h3 style={h3Style}>{t("insightsPaymentFrequencyTitle")}</h3>
        <p style={paragraphStyle}>{t("insightsPaymentFrequencyContent")}</p>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t("detailsTitle")}</h2>

        <h3 style={h3Style}>{t("detailsSemiAnnualTitle")}</h3>
        <p style={paragraphStyle}>{t("detailsSemiAnnualContent")}</p>

        <h3 style={h3Style}>{t("detailsCmhcRatesTitle")}</h3>
        <p style={paragraphStyle}>{t("detailsCmhcRatesContent")}</p>
        <ul style={listStyle}>
          <li style={listItemStyle}>{t("detailsCmhcRate1")}</li>
          <li style={listItemStyle}>{t("detailsCmhcRate2")}</li>
          <li style={listItemStyle}>{t("detailsCmhcRate3")}</li>
          <li style={listItemStyle}>{t("detailsCmhcRate4")}</li>
        </ul>

        <h3 style={h3Style}>{t("detailsClosingCostsTitle")}</h3>
        <p style={paragraphStyle}>{t("detailsClosingCostsContent")}</p>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t("faqTitle")}</h2>

        <div style={faqItemStyle}>
          <h3 style={faqQuestionStyle}>{t("faq1Question")}</h3>
          <p style={faqAnswerStyle}>{t("faq1Answer")}</p>
        </div>

        <div style={faqItemStyle}>
          <h3 style={faqQuestionStyle}>{t("faq2Question")}</h3>
          <p style={faqAnswerStyle}>{t("faq2Answer")}</p>
        </div>

        <div style={faqItemStyle}>
          <h3 style={faqQuestionStyle}>{t("faq3Question")}</h3>
          <p style={faqAnswerStyle}>{t("faq3Answer")}</p>
        </div>

        <div style={faqItemStyle}>
          <h3 style={faqQuestionStyle}>{t("faq4Question")}</h3>
          <p style={faqAnswerStyle}>{t("faq4Answer")}</p>
        </div>

        <div style={faqItemStyle}>
          <h3 style={faqQuestionStyle}>{t("faq5Question")}</h3>
          <p style={faqAnswerStyle}>{t("faq5Answer")}</p>
        </div>

        <div style={faqItemStyle}>
          <h3 style={faqQuestionStyle}>{t("faq6Question")}</h3>
          <p style={faqAnswerStyle}>{t("faq6Answer")}</p>
        </div>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t("referencesTitle")}</h2>
        <ol style={listStyle}>
          <li style={listItemStyle}>
            <a href="https://wowa.ca/calculators/affordability" target="_blank" rel="noopener noreferrer" style={refLinkStyle}>
              {t("reference1")}
            </a>
          </li>
          <li style={listItemStyle}>
            <a href="https://www.ratehub.ca/mortgage-affordability-calculator" target="_blank" rel="noopener noreferrer" style={refLinkStyle}>
              {t("reference2")}
            </a>
          </li>
          <li style={listItemStyle}>
            <a href="https://www.canada.ca/en/financial-consumer-agency/services/mortgages/mortgage-calculator.html" target="_blank" rel="noopener noreferrer" style={refLinkStyle}>
              {t("reference3")}
            </a>
          </li>
          <li style={listItemStyle}>
            <a href="https://www.cmhc-schl.gc.ca/" target="_blank" rel="noopener noreferrer" style={refLinkStyle}>
              {t("reference4")}
            </a>
          </li>
        </ol>
      </section>
    </div>
  );
}